import React from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FadeIn } from '../components/ui/FadeIn';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function EventsPage() {
  const {
    t,
    isRTL
  } = useLanguage();
  const upcomingEvents = [{
    title: t('events.upcoming.spring.title'),
    date: t('events.upcoming.spring.date'),
    location: t('events.upcoming.spring.location'),
    desc: t('events.upcoming.spring.description')
  }, {
    title: t('events.upcoming.autumn.title'),
    date: t('events.upcoming.autumn.date'),
    location: t('events.upcoming.autumn.location'),
    desc: t('events.upcoming.autumn.description')
  }];
  const pastEvents = [{
    title: 'Big Causes 2019',
    year: '2019',
    image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Annual fundraising and networking event supporting major charitable initiatives.'
  }, {
    title: 'Big Causes 2018',
    year: '2018',
    image: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Community gathering focused on healthcare development projects.'
  }, {
    title: 'Big Causes 2017',
    year: '2017',
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Bringing together professionals for a night of giving and connection.'
  }, {
    title: 'Big Causes 2016',
    year: '2016',
    image: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    desc: 'Our inaugural major fundraising gala supporting medical education.'
  }];
  return <>
      <SEO title={t('nav.events')} description={t('events.hero.subtitle')} data-id="element-354" />

      <div className="bg-[#1e5f4f] text-white py-16 md:py-20" data-id="element-355">
        <div className="container mx-auto px-4 text-center" data-id="element-356">
          <FadeIn data-id="element-357">
            <Calendar className="h-12 w-12 mx-auto mb-6 text-white" data-id="element-358" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-white" data-id="element-359">
              Events & Gatherings
            </h1>
            <p className="text-xl text-white max-w-2xl mx-auto" data-id="element-360">
              Join us for professional networking, cultural celebrations, and
              community building events throughout the year.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Upcoming Events Section */}
      <Section data-id="element-361">
        <div className="text-center mb-12" data-id="element-362">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4" data-id="element-363">
            {t('events.upcoming.title')}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-364">
            {t('events.upcoming.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-365">
          {upcomingEvents.map((event, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-366">
              <Card className="flex flex-col h-full overflow-hidden hover:shadow-xl transition-shadow border-2 border-primary-100" data-id="element-367">
                <CardContent className="flex-1 p-8 flex flex-col text-center" data-id="element-368">
                  <div className="h-16 w-16 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-6 mx-auto" data-id="element-369">
                    <Calendar className="h-8 w-8" data-id="element-370" />
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2" data-id="element-371">
                    {event.title}
                  </h3>
                  <div className="text-accent-700 font-bold text-lg mb-4" data-id="element-372">
                    {event.date}
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 mb-6 flex-1" data-id="element-373">
                    <div className="flex items-center justify-center" data-id="element-374">
                      <MapPin className={`h-4 w-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-375" />
                      {event.location}
                    </div>
                  </div>
                  <p className="text-gray-600 italic" data-id="element-376">{event.desc}</p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Past Events */}
      <Section background="gray" data-id="element-377">
        <div className="text-center mb-12" data-id="element-378">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4" data-id="element-379">
            {t('events.past.title')}
          </h2>
          <p className="text-gray-600" data-id="element-380">{t('events.past.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-381">
          {pastEvents.map((event, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-382">
              <Card className="h-full hover:shadow-md transition-shadow overflow-hidden" data-id="element-383">
                <div className="h-48 overflow-hidden bg-gray-200" data-id="element-384">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-385" />
                </div>
                <CardHeader className="pb-3" data-id="element-386">
                  <CardTitle className="text-lg leading-tight" data-id="element-387">
                    {event.title}
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-388">
                  <p className="text-sm text-gray-600" data-id="element-389">{event.desc}</p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Member Benefits CTA */}
      <Section className="text-center" data-id="element-390">
        <FadeIn data-id="element-391">
          <div className="max-w-3xl mx-auto" data-id="element-392">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4" data-id="element-393">
              {t('events.cta.title')}
            </h2>
            <p className="text-gray-600 mb-8 text-lg" data-id="element-394">
              {t('events.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-395">
              <Link to="/membership" data-id="element-396">
                <Button size="lg" data-id="element-397">{t('events.cta.joinButton')}</Button>
              </Link>
              <Link to="/about" data-id="element-398">
                <Button variant="outline" size="lg" data-id="element-399">
                  {t('events.cta.learnButton')}
                </Button>
              </Link>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
}